import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { bindingAccount, getPaymentCredentials, getTransactionResult, updateTransactionStatus } from '../../libs/GsmpiApi';
import { POST_MESSAGE_CONTENT_MESSAGE, TRANSACTION_STATUS, POLLING_TIME } from '../../common/constants';
import CancelButton from "../../components/buttons/CancelButton";
import SamsungPayHeader from "../../components/common/SamsungPayHeader";
import SamsungPayBox from "../../components/common/SamsungPayBox";
import { useSelector } from 'react-redux';
import { timeFormat } from "../../utils/timeFormat";
import "../../css/pc/Timer.css";
import { getCookieDetails } from '../../utils/cookieDetails';
import { clearInterval, setInterval } from 'worker-timers';
import debug from 'debug';

const log = debug('app:TimerPage');

export default function TimerPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const location = useLocation();
  const saEmail = location.state?.email;
  const useCookie = location.state?.useCookie;
  const useAccount = location.state?.useAccount;

  const [statusUpdateCountDown, setStatusUpdateCountDown] = useState(POLLING_TIME.EMAIL_POLL);
  const [statusPollingCountDown, setStatusPollingCountDown] = useState(POLLING_TIME.EMAIL_POLL);
  const [transactionRetried, setTransactionRetried] = useState(false);
  const abortControllersRef = useRef([]);

  const { serviceId, transactionId, domains } = useSelector((state) => state.paymentClient);
  const browserId = useSelector((state) => state.browserDetails.browserId);

  function handleConfirmedTransaction(refId, cookieDetails) {
    getPaymentCredentials({refId: refId, serviceId: serviceId})
      .then((result) => {
        const state = {
          state: {
            paymentCredential: result.data,
            cookie: cookieDetails,
            referenceId: refId
          }
        };

        navigate('/gsmpi-web/done', state);
      })
      .catch(() => navigate('/gsmpi-web/error'));
  }

  useEffect(() => {
    // Countdown responsible for updating the transaction's status to expired when the timer reaches 0
    if (statusUpdateCountDown > 0) {
      const timerInterval = setInterval(() => {
        setStatusUpdateCountDown(prevCountDown => prevCountDown > 0 ? prevCountDown - 1 : 0);
      }, 1000);

      return () => clearInterval(timerInterval);
    } else {
      cancelPollingRequests();
      const timedOutPayload = {
        payment: {
          status: TRANSACTION_STATUS.EXPIRED,
          reason: 'transactionExpired'
        },
        timestamp: new Date().getTime()
      };

      updateTransactionStatus(transactionId, timedOutPayload)
        .then(() => {
          navigate('/gsmpi-web/timeout', {
            state: {
              timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
            }
          });
        })
        .catch((error) => {
          log(`Error occurred when updating the transaction status: ${error}`);

          navigate('/gsmpi-web/timeout', {
            state: {
              timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
            }
          });
        });
    }
  }, [statusUpdateCountDown]);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllersRef.current.push(abortController);
    const abortSignal = abortController.signal;

    // Countdown that continuously polls the status of the transaction then does redirection based on the polled status
    if (statusPollingCountDown > 0) {
      const pollingInterval = setInterval(() => {
        setStatusPollingCountDown(prevStatusPollingCountDown => prevStatusPollingCountDown > 0 ? prevStatusPollingCountDown - 1.5 : 0);
        getTransactionResult(transactionId, false, abortSignal)
          .then(result => {
            abortControllersRef.current = abortControllersRef.current.filter(controller => controller !== abortController);
            handlePollingResult(result);
          })
          .catch(error => {
            handlePollingError(error);
          });
      }, 1500);

      return () => clearInterval(pollingInterval);
    }
  }, [statusPollingCountDown]);

  const retryTransaction = async function(e) {
    e.preventDefault();

    if (!transactionRetried) {
      const bindAccountRequest = {
        serviceId: serviceId,
        saEmail: saEmail,
        browserId: browserId,
        domains: domains,
        useCookie: useCookie,
        useAccount: useAccount
      };

      await bindingAccount(transactionId, bindAccountRequest)
        .then(() => {
          setStatusUpdateCountDown(POLLING_TIME.EMAIL_POLL);
          setStatusPollingCountDown(POLLING_TIME.EMAIL_POLL);
          setTransactionRetried(true);
        }).catch(() => {
          navigate('/gsmpi-web/error');
        });
    }
  };

  const cancelPollingRequests = () => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current = [];
  };

  const handlePollingResult = (result) => {
    if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
      cancelPollingRequests();
      handleConfirmedTransaction(result.data.refId, getCookieDetails(result.headers.get("x-smps-gsmpi-cookie-id")));
      setStatusPollingCountDown(0);
    } else if (result.data.status === TRANSACTION_STATUS.EXPIRED) {
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (result.data.status === TRANSACTION_STATUS.CANCELED) {
      navigate('/gsmpi-web/cancel');
    } else if (result.data.status === TRANSACTION_STATUS.ERRED) {
      navigate('/gsmpi-web/error');
    } else if (result.data.status === TRANSACTION_STATUS.APP_UPDATE_REQUIRED) {
      navigate(`/gsmpi-web/update`);
    } else {
      log(`result status : ${result.data.status}`);
    }
  };

  const handlePollingError = (error) => {
    if (error.response.data.resultCode === 'OPM1N1041') {
      // retry count exceeded
      const timedOutPayload = {
        payment: {
          status: TRANSACTION_STATUS.EXPIRED,
          reason: 'exceededRetryAmount'
        },
        timestamp: new Date().getTime()
      };

      updateTransactionStatus(transactionId, timedOutPayload)
        .then(() => navigate('/gsmpi-web/timeout', {
          state: {
            timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_EXCEEDED_RETRY_LIMIT
          }
        }));
    } else if (error.response.data.resultCode === 'OPM2N1101' || error.response.data.resultCode === 'OPM1N1039') {
      //transaction is expired
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (error.response.data.resultCode === 'OPM2N1103' || error.response.data.resultCode === 'OPM2N1104') {
      //transaction is invalid
      navigate('/gsmpi-web/error');
    } else if (error.response.data.resultCode === 'OPM1N1046') {
      //app needs update
      navigate(`/gsmpi-web/update`);
    } else {
      navigate('/gsmpi-web/error');
    }
  };

  return (
    <SamsungPayBox>
      <SamsungPayHeader headerMessage="pay.gsmpi.done.msg1"/>
      <div className="mpi-content mpi-step-timer">
        <p className="mpi-timer-text">{t('pay.gsmpi.timeRemaining.msg3')}</p>
        <div className="mpi-timer-time">{timeFormat(statusUpdateCountDown)}</div>
      </div>
      <div className="mpi-boxbottom">
        <div className="mpi-timer-info" id="retry-push">
          {!transactionRetried && t('pay.gsmpi.timeRemaining.notification')}
          &nbsp;
          {
            !transactionRetried &&
            <a className="mpi-reask" onClick={retryTransaction}>
              {t('pay.gsmpi.timeRemaining.button1')}
            </a>
          }
        </div>
        <CancelButton className="mpi-button-cancel2" transactionId={transactionId}/>
      </div>
    </SamsungPayBox>
  );
}